"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { MergedCandidate } from "@/types/dao/election";
import { ElectionRow } from "./ElectionRow";
import { formatVoteCount } from "./VoteCountBadge";

const PAGE_SIZE = 10;

interface ElectionRowsListProps {
  candidates: MergedCandidate[];
  interactive?: boolean;
  ranked?: boolean;
}

/**
 * Election reads return complete chain arrays. Reveal rows in batches while
 * retaining the full vote-sorted dataset and the contract's member ranks.
 * Callers key this list by cycle so changing years resets the visible batch.
 */
export const ElectionRowsList: React.FC<ElectionRowsListProps> = ({
  candidates,
  interactive = true,
  ranked = false,
}) => {
  const t = useTranslations("common");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { ref } = useInView({
    rootMargin: "200px",
    onChange: (inView) => {
      if (inView) {
        setVisibleCount((count) =>
          Math.min(count + PAGE_SIZE, candidates.length),
        );
      }
    },
  });
  const hasMore = visibleCount < candidates.length;

  const tileDigits = candidates.reduce(
    (max, candidate) =>
      Math.max(max, formatVoteCount(candidate.totalReceivedVoteDnmWei).length),
    4,
  );

  return (
    <div
      className={cn(
        "flex flex-col",
        interactive && !ranked ? "gap-3" : "gap-2",
      )}
    >
      {candidates.slice(0, visibleCount).map((candidate, index) => (
        <ElectionRow
          key={candidate.walletAddress}
          candidate={candidate}
          rank={ranked ? index + 1 : undefined}
          interactive={interactive}
          voteTileDigitsCh={tileDigits}
        />
      ))}
      {hasMore && (
        <div key={visibleCount} ref={ref} className="flex justify-center py-4">
          <Button
            variant="light"
            colors="accent"
            size="sm"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          >
            {t("viewMore")}
          </Button>
        </div>
      )}
    </div>
  );
};
